import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { useResolvedVoiceLanguage, type VoiceLanguageCode } from "./useResolvedVoiceLanguage";
import "./voice-beacon.css";

type LanguageCode = VoiceLanguageCode;
type VoiceState =
  | "collapsed" | "idle" | "connecting" | "listening"
  | "thinking" | "speaking" | "text" | "email-approval" | "error"
  | "mic-prompt" | "mic-blocked";

type MicPermission = "unknown" | "prompt" | "granted" | "denied" | "unsupported";

export interface VoiceBeaconProps {
  tenantId?: string;
  projectId?: string;
  platformName?: string;
  agentName?: string;
  defaultLanguage?: LanguageCode;
}

interface AgentMessage { role: "user" | "assistant" | "system"; content: string; ts: number }
interface EmailDraft { to: string; subject: string; body: string; status: "draft"; consentRequired: true }
interface AgentResponse {
  answer: string;
  language?: LanguageCode;
  confidence?: "low" | "medium" | "high";
  sources?: Array<{ id: string; title: string; url?: string; verifyCurrent?: boolean; trustLevel?: number }>;
  emailDraft?: EmailDraft;
  conversationId?: string;
}

// Single-tenant defaults seeded by the migration. These are public ids.
const DEFAULT_TENANT_ID = "00000000-0000-0000-0000-00000000b007";
const DEFAULT_PROJECT_ID = "00000000-0000-0000-0000-0000b007b007";
const STORAGE_KEY = "bp.voice-beacon.cid";

function getStarters(t: (k: string) => string): string[] {
  return [
    t("starters.offers"),
    t("starters.pricing"),
    t("starters.demo"),
    t("starters.support"),
    t("starters.needs"),
  ];
}

function detectBrowserHint(t: (k: string) => string): string {
  if (typeof navigator === "undefined") return t("mic.hintGeneric");
  const ua = navigator.userAgent;
  if (/Edg|Chrome|Chromium/.test(ua) && !/OPR|Opera/.test(ua)) return t("mic.hintChrome");
  if (/Safari/.test(ua) && !/Chrome|Chromium/.test(ua)) return t("mic.hintSafari");
  if (/Firefox/.test(ua)) return t("mic.hintFirefox");
  return t("mic.hintGeneric");
}

export function VoiceBeacon(props: VoiceBeaconProps = {}) {
  const { t, i18n } = useTranslation("voice-agent");

  const tenantId = props.tenantId ?? DEFAULT_TENANT_ID;
  const projectId = props.projectId ?? DEFAULT_PROJECT_ID;
  const platformName = props.platformName ?? "Bootpreneurs";
  const agentName = props.agentName ?? "Bootie";
  const supported: LanguageCode[] = ["fr", "en", "es", "pt"];
  const { language: lang } = useResolvedVoiceLanguage(projectId, supported, props.defaultLanguage ?? "fr");
  const defaultLanguage = lang;

  // Switch i18next to the resolved voice-agent language so widget chrome localizes
  // automatically (won't override an explicit app-wide locale change).
  useEffect(() => {
    const current = i18n.language?.split("-")[0]?.toLowerCase();
    if (current !== lang) {
      void i18n.changeLanguage(lang);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang]);

  const [state, setState] = useState<VoiceState>("collapsed");
  const [messages, setMessages] = useState<AgentMessage[]>([]);
  const [text, setText] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [trust, setTrust] = useState<string>(t("trust.ready"));
  const [emailDraft, setEmailDraft] = useState<EmailDraft | null>(null);
  const [emailEventId, setEmailEventId] = useState<string | null>(null);
  const [micPermission, setMicPermission] = useState<MicPermission>("unknown");
  const [voiceAvailable, setVoiceAvailable] = useState<boolean>(true);
  const [conversationId, setConversationId] = useState<string | undefined>(() => {
    try { return localStorage.getItem(STORAGE_KEY) ?? undefined; } catch { return undefined; }
  });

  const peerRef = useRef<RTCPeerConnection | null>(null);
  const channelRef = useRef<RTCDataChannel | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const sessionStartedAt = useRef<number>(0);

  // Probe mic permission once on mount (best-effort — Safari throws on this query).
  useEffect(() => {
    if (typeof navigator === "undefined") return;
    const md = navigator.mediaDevices;
    if (!md || typeof md.getUserMedia !== "function") {
      setMicPermission("unsupported");
      setVoiceAvailable(false);
      return;
    }
    if (!navigator.permissions?.query) {
      setMicPermission("prompt");
      return;
    }
    navigator.permissions
      .query({ name: "microphone" as PermissionName })
      .then((status) => {
        setMicPermission(status.state as MicPermission);
        status.onchange = () => setMicPermission(status.state as MicPermission);
      })
      .catch(() => setMicPermission("prompt"));
  }, []);

  // Sync default trust label with locale changes.
  useEffect(() => { setTrust(t("trust.ready")); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [i18n.language]);

  useEffect(() => {
    if (conversationId) {
      try { localStorage.setItem(STORAGE_KEY, conversationId); } catch { /* noop */ }
    }
  }, [conversationId]);

  const starters = useMemo(() => getStarters((k) => t(k)), [t, i18n.language]);
  const agentLabel = agentName ?? t("label");

  async function invoke<T = any>(name: string, body: Record<string, unknown>): Promise<T> {
    const { data, error: invokeError } = await supabase.functions.invoke(name, { body });
    if (invokeError) throw invokeError;
    if ((data as any)?.error) throw new Error((data as any).error);
    return data as T;
  }

  async function startVoice() {
    // Pre-flight permission UX
    if (micPermission === "denied") { setState("mic-blocked"); return; }
    if (micPermission === "unsupported") {
      setVoiceAvailable(false);
      setError(t("mic.noDevice"));
      setState("text");
      return;
    }
    if (micPermission === "prompt" || micPermission === "unknown") {
      setState("mic-prompt");
      return;
    }
    await connectVoice();
  }

  async function connectVoice() {
    setState("connecting");
    setError(null);
    sessionStartedAt.current = performance.now();
    try {
      const session = await invoke<any>("voice-session-create", {
        tenantId, projectId, platformName, agentName, defaultLanguage,
      });
      const clientSecret = session?.realtime?.client_secret?.value
        ?? session?.realtime?.value
        ?? session?.realtime?.session?.client_secret?.value;
      if (!clientSecret) throw new Error(t("voiceUnavailable"));

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      setMicPermission("granted");
      const pc = new RTCPeerConnection();
      peerRef.current = pc;
      const audio = audioRef.current ?? document.createElement("audio");
      audio.autoplay = true;
      audioRef.current = audio;
      pc.ontrack = (event) => { audio.srcObject = event.streams[0]; setState("speaking"); };
      stream.getTracks().forEach((track) => pc.addTrack(track, stream));
      const channel = pc.createDataChannel("oai-events");
      channelRef.current = channel;
      channel.onmessage = (event) => handleRealtimeEvent(event.data);
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);

      const model = session?.realtime?.session?.model ?? "gpt-realtime";
      const r = await fetch(`https://api.openai.com/v1/realtime?model=${encodeURIComponent(model)}`, {
        method: "POST",
        headers: { authorization: `Bearer ${clientSecret}`, "content-type": "application/sdp" },
        body: offer.sdp ?? "",
      });
      if (!r.ok) throw new Error(`Realtime ${r.status}`);
      await pc.setRemoteDescription({ type: "answer", sdp: await r.text() });
      setState("listening");
      setTrust(t("trust.live", { ms: Math.round(performance.now() - sessionStartedAt.current) }));
    } catch (err) {
      const e = err as DOMException & { name?: string };
      console.warn("voice startup failed, falling back to text:", err);
      if (e?.name === "NotAllowedError" || e?.name === "SecurityError") {
        setMicPermission("denied");
        setState("mic-blocked");
        setError(t("voiceMicDenied"));
      } else if (e?.name === "NotFoundError" || e?.name === "OverconstrainedError" || e?.name === "DevicesNotFoundError") {
        setMicPermission("unsupported");
        setVoiceAvailable(false);
        setState("text");
        setError(t("mic.noDevice"));
      } else {
        setState("text");
        setError(t("voiceUnavailable"));
      }
    }
  }

  async function tryMicAgain() {
    setError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach((tr) => tr.stop());
      setMicPermission("granted");
      await connectVoice();
    } catch (err) {
      const e = err as DOMException;
      if (e?.name === "NotAllowedError" || e?.name === "SecurityError") {
        setMicPermission("denied");
        setState("mic-blocked");
      } else {
        setVoiceAvailable(false);
        setState("text");
        setError(t("mic.noDevice"));
      }
    }
  }

  function useTextFallback() {
    setState("text");
    setError(null);
  }

  function stopVoice() {
    peerRef.current?.getSenders().forEach((s) => s.track?.stop());
    peerRef.current?.close();
    peerRef.current = null;
    channelRef.current = null;
    setState("idle");
  }

  async function sendText(value = text) {
    const message = value.trim();
    if (!message) return;
    setText("");
    setError(null);
    setState("thinking");
    setMessages((prev) => [...prev, { role: "user", content: message, ts: Date.now() }]);
    try {
      const response = await invoke<AgentResponse>("voice-agent-chat", {
        tenantId, projectId, conversationId, message, languageHint: lang,
      });
      if (response.conversationId && response.conversationId !== conversationId) {
        setConversationId(response.conversationId);
      }
      setMessages((prev) => [...prev, { role: "assistant", content: response.answer, ts: Date.now() }]);
      const count = response.sources?.length ?? 0;
      const conf = response.confidence ?? "medium";
      setTrust(count
        ? t("trust.sources", { count, confidence: conf })
        : t("states.idle"));
      if (response.emailDraft) {
        try {
          const draftEvent = await invoke<{ id: string }>("voice-email-draft", {
            tenantId, projectId, conversationId: response.conversationId ?? conversationId,
            message, summary: response.answer,
          });
          setEmailDraft(response.emailDraft);
          setEmailEventId(draftEvent.id);
          setState("email-approval");
          return;
        } catch (draftErr) {
          console.warn("email draft failed:", draftErr);
        }
      }
      setState("text");
    } catch (err) {
      console.error("voice-agent-chat failed:", err);
      setError((err as Error).message);
      setState("error");
    }
  }

  async function approveEmail() {
    if (!emailEventId) return;
    try {
      await invoke("voice-email-send", { tenantId, projectId, emailEventId, approved: true });
      setMessages((prev) => [...prev, { role: "system", content: t("draft.queued"), ts: Date.now() }]);
      setEmailDraft(null);
      setEmailEventId(null);
      setState("text");
    } catch (err) {
      setError((err as Error).message);
      setState("error");
    }
  }

  function cancelEmail() {
    setEmailDraft(null);
    setEmailEventId(null);
    setState("text");
  }

  function handleRealtimeEvent(raw: string) {
    try {
      const event = JSON.parse(raw);
      if (typeof event.type !== "string") return;
      if (event.type.includes("speech_started")) setState("listening");
      if (event.type.includes("response.created")) setState("thinking");
      if (event.type.includes("response.audio")) setState("speaking");
      const transcript = event.transcript ?? event.delta ?? event.text;
      if (event.type.includes("transcript") && transcript) {
        setMessages((prev) => [...prev, { role: "assistant", content: String(transcript), ts: Date.now() }]);
      }
    } catch { /* ignore unknown frames */ }
  }

  if (state === "collapsed") {
    return (
      <button
        type="button"
        className="voice-beacon voice-beacon--collapsed"
        onClick={() => setState("idle")}
        aria-label={t("open")}
      >
        <span className="voice-beacon__pulse" aria-hidden="true" />
        <span>{agentLabel}</span>
      </button>
    );
  }

  return (
    <section
      className={`voice-beacon voice-beacon--panel voice-beacon--${state}`}
      aria-label={`${platformName} AI voice assistant`}
    >
      <div className="voice-beacon__wave" aria-hidden="true" />
      <aside className="voice-beacon__level" aria-hidden="true">
        <span /><span /><span /><span />
      </aside>
      <header className="voice-beacon__header">
        <div>
          <span className="voice-beacon__eyebrow">{t("eyebrow")}</span>
          <strong>{agentLabel}</strong>
        </div>
        <button type="button" className="voice-beacon__close" onClick={() => setState("collapsed")} aria-label={t("close")}>×</button>
      </header>
      <div className="voice-beacon__status">
        <span>{t(`states.${state}` as never, t("states.idle"))}</span>
        <small>{trust}</small>
      </div>
      <div className="voice-beacon__starters" aria-label="Conversation starters">
        {starters.map((s) => (
          <button key={s} type="button" onClick={() => void sendText(s)} disabled={state === "thinking" || state === "connecting"}>
            {s}
          </button>
        ))}
      </div>
      {error ? <p className="voice-beacon__error" role="alert">{error}</p> : null}
      <div className="voice-beacon__messages" aria-live="polite">
        {messages.slice(-6).map((m) => (
          <p key={m.ts} className={`voice-beacon__message voice-beacon__message--${m.role}`}>
            {m.content}
          </p>
        ))}
      </div>
      {emailDraft ? (
        <div className="voice-beacon__draft" role="region" aria-label={t("draft.title")}>
          <strong>{t("draft.title")}</strong>
          <span>{t("draft.to")}: {emailDraft.to}</span>
          <p>{emailDraft.subject}</p>
          <div style={{ display: "flex", gap: "8px" }}>
            <button type="button" onClick={() => void approveEmail()} disabled={!emailEventId}>{t("draft.approve")}</button>
            <button type="button" onClick={cancelEmail} style={{ background: "transparent", color: "inherit" }}>{t("draft.cancel")}</button>
          </div>
        </div>
      ) : null}
      {state === "mic-prompt" ? (
        <div className="voice-beacon__draft" role="region" aria-label={t("mic.prePromptTitle")}>
          <strong>{t("mic.prePromptTitle")}</strong>
          <p style={{ margin: "4px 0 8px", fontSize: 13, opacity: 0.85 }}>{t("mic.prePromptBody")}</p>
          <div style={{ display: "flex", gap: "8px" }}>
            <button type="button" onClick={() => void connectVoice()}>{t("mic.allow")}</button>
            <button type="button" onClick={useTextFallback} style={{ background: "transparent", color: "inherit" }}>{t("mic.useText")}</button>
          </div>
        </div>
      ) : null}
      {state === "mic-blocked" ? (
        <div className="voice-beacon__draft" role="alert" aria-label={t("mic.blockedTitle")}>
          <strong>{t("mic.blockedTitle")}</strong>
          <p style={{ margin: "4px 0 4px", fontSize: 13, opacity: 0.85 }}>{t("mic.blockedBody")}</p>
          <p style={{ margin: "0 0 8px", fontSize: 12, opacity: 0.7 }}>{detectBrowserHint(t)}</p>
          <div style={{ display: "flex", gap: "8px" }}>
            <button type="button" onClick={() => void tryMicAgain()}>{t("mic.tryAgain")}</button>
            <button type="button" onClick={useTextFallback} style={{ background: "transparent", color: "inherit" }}>{t("mic.useText")}</button>
          </div>
        </div>
      ) : null}
      <form
        className="voice-beacon__composer"
        onSubmit={(event) => { event.preventDefault(); void sendText(); }}
      >
        <input
          value={text}
          onChange={(event) => setText(event.target.value)}
          placeholder={t("placeholder")}
          aria-label={t("placeholder")}
          disabled={state === "thinking" || state === "connecting"}
        />
        <button type="submit" disabled={state === "thinking" || !text.trim()}>{t("send")}</button>
      </form>
      <button
        className="voice-beacon__mic"
        type="button"
        onClick={state === "listening" || state === "speaking" ? stopVoice : startVoice}
        aria-label="Toggle voice"
        disabled={!voiceAvailable}
        title={!voiceAvailable ? t("mic.noDevice") : undefined}
        style={!voiceAvailable ? { opacity: 0.4, cursor: "not-allowed" } : undefined}
      >
        <span aria-hidden="true" />
      </button>
      <p className="voice-beacon__disclaimer" aria-hidden="false" style={{ fontSize: 11, opacity: 0.6, margin: "6px 0 0", padding: "0 16px 14px" }}>
        {t("disclaimer")}
      </p>
    </section>
  );
}

export default VoiceBeacon;